import type {
  ObservationDataset,
  ObservationRow,
} from '../contracts/index.ts';
import { observationDatasetSchema } from '../contracts/index.ts';

export type ObservationStoreListener = (
  dataset: ObservationDataset | null,
  datasetId: string,
) => void;

export class ObservationRevisionConflictError extends Error {
  constructor(
    readonly datasetId: string,
    readonly currentRevision: number,
    readonly incomingRevision: number,
  ) {
    super(
      `观察数据 ${datasetId} 的更新版本过期：当前 ${currentRevision}，收到 ${incomingRevision}`,
    );
    this.name = 'ObservationRevisionConflictError';
  }
}

function cloneDataset(dataset: ObservationDataset): ObservationDataset {
  return structuredClone(dataset);
}

function comparableTime(value: ObservationRow['values'][string]): number | null {
  if (typeof value === 'number' && Number.isFinite(value))
    return value;
  if (typeof value !== 'string')
    return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function applyRetention(dataset: ObservationDataset): ObservationDataset {
  const retention = dataset.retention;
  if (!retention || dataset.rows.length === 0)
    return dataset;

  let rows = dataset.rows;
  if (retention.timeWindow !== undefined && retention.timeField) {
    const times = rows
      .map(row => comparableTime(row.values[retention.timeField!]))
      .filter((value): value is number => value !== null);
    if (times.length > 0) {
      const cutoff = Math.max(...times) - retention.timeWindow;
      rows = rows.filter((row) => {
        const time = comparableTime(row.values[retention.timeField!]);
        return time === null || time >= cutoff;
      });
    }
  }
  if (retention.maxRows !== undefined && rows.length > retention.maxRows)
    rows = rows.slice(-retention.maxRows);
  return rows === dataset.rows ? dataset : { ...dataset, rows };
}

export class ObservationStore {
  private readonly datasets = new Map<string, ObservationDataset>();
  private readonly listeners = new Map<string, Set<ObservationStoreListener>>();
  private readonly allListeners = new Set<ObservationStoreListener>();
  private batchDepth = 0;
  private pendingNotifications = new Map<
    string,
    ObservationDataset | null
  >();

  apply(rawDataset: ObservationDataset): ObservationDataset {
    const incoming = observationDatasetSchema.parse(rawDataset);
    if (incoming.updateMode === 'append')
      return this.append(incoming);
    if (incoming.updateMode === 'patch')
      return this.patch(incoming);
    return this.replace(incoming);
  }

  set(rawDataset: ObservationDataset): ObservationDataset {
    return this.apply(rawDataset);
  }

  applyBatch(rawDatasets: ObservationDataset[]): ObservationDataset[] {
    if (rawDatasets.length === 0)
      return [];
    const rootBatch = this.batchDepth === 0;
    const snapshot = rootBatch
      ? new Map(
          [...this.datasets.entries()].map(([id, dataset]) => [
            id,
            cloneDataset(dataset),
          ]),
        )
      : null;
    const pendingBefore = rootBatch
      ? new Map(this.pendingNotifications)
      : null;
    this.batchDepth += 1;
    let succeeded = false;
    try {
      const committed = rawDatasets.map(dataset => this.apply(dataset));
      succeeded = true;
      return committed;
    }
    catch (error) {
      if (rootBatch && snapshot) {
        this.datasets.clear();
        for (const [id, dataset] of snapshot)
          this.datasets.set(id, dataset);
        this.pendingNotifications = pendingBefore ?? new Map();
      }
      throw error;
    }
    finally {
      this.batchDepth -= 1;
      if (rootBatch && succeeded)
        this.flushNotifications();
    }
  }

  replace(rawDataset: ObservationDataset): ObservationDataset {
    const dataset = observationDatasetSchema.parse({
      ...rawDataset,
      updateMode: 'replace',
    });
    return this.commit(dataset.id, dataset);
  }

  append(rawDataset: ObservationDataset): ObservationDataset {
    const incoming = observationDatasetSchema.parse(rawDataset);
    const previous = this.datasets.get(incoming.id);
    if (!previous)
      return this.commit(incoming.id, { ...incoming, updateMode: 'replace' });

    this.assertRevision(previous, incoming);
    this.assertCompatible(previous, incoming);
    const rowsById = new Map(previous.rows.map(row => [row.id, row]));
    for (const row of incoming.rows)
      rowsById.set(row.id, row);

    return this.commit(incoming.id, applyRetention({
      ...previous,
      rows: [...rowsById.values()],
      metadata: { ...previous.metadata, ...incoming.metadata },
      revision: incoming.revision,
      updateMode: 'append',
      removedRowIds: [],
      retention: incoming.retention ?? previous.retention,
    }));
  }

  patch(rawDataset: ObservationDataset): ObservationDataset {
    const incoming = observationDatasetSchema.parse(rawDataset);
    const previous = this.datasets.get(incoming.id);
    if (!previous)
      return this.commit(incoming.id, { ...incoming, updateMode: 'replace' });

    this.assertRevision(previous, incoming);
    this.assertCompatible(previous, incoming);
    const removedIds = new Set(incoming.removedRowIds ?? []);
    const patches = new Map(incoming.rows.map(row => [row.id, row]));
    const rows = previous.rows.flatMap((row) => {
      if (removedIds.has(row.id))
        return [];
      const patch = patches.get(row.id);
      if (!patch)
        return [row];
      patches.delete(row.id);
      return [{
        id: row.id,
        values: { ...row.values, ...patch.values },
      }];
    });

    return this.commit(incoming.id, applyRetention({
      ...previous,
      rows: [...rows, ...patches.values()],
      metadata: { ...previous.metadata, ...incoming.metadata },
      revision: incoming.revision,
      updateMode: 'patch',
      removedRowIds: [],
      retention: incoming.retention ?? previous.retention,
    }));
  }

  get(datasetId: string): ObservationDataset | undefined {
    const dataset = this.datasets.get(datasetId);
    return dataset ? cloneDataset(dataset) : undefined;
  }

  getRows(datasetId: string, limit?: number): ObservationRow[] {
    const rows = this.datasets.get(datasetId)?.rows ?? [];
    return structuredClone(limit ? rows.slice(-limit) : rows);
  }

  has(datasetId: string): boolean {
    return this.datasets.has(datasetId);
  }

  getRevision(datasetId: string): number | undefined {
    return this.datasets.get(datasetId)?.revision;
  }

  remove(datasetId: string): void {
    if (!this.datasets.delete(datasetId))
      return;
    this.notify(datasetId, null);
  }

  clear(): void {
    const ids = [...this.datasets.keys()];
    this.datasets.clear();
    for (const id of ids)
      this.notify(id, null);
  }

  subscribe(
    datasetId: string,
    listener: ObservationStoreListener,
  ): () => void {
    const listeners = this.listeners.get(datasetId) ?? new Set();
    listeners.add(listener);
    this.listeners.set(datasetId, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0)
        this.listeners.delete(datasetId);
    };
  }

  subscribeAll(listener: ObservationStoreListener): () => void {
    this.allListeners.add(listener);
    return () => this.allListeners.delete(listener);
  }

  toRecord(): Record<string, ObservationDataset> {
    return Object.fromEntries(
      [...this.datasets.entries()].map(([id, dataset]) => [
        id,
        cloneDataset(dataset),
      ]),
    );
  }

  private assertCompatible(
    previous: ObservationDataset,
    incoming: ObservationDataset,
  ): void {
    if (
      previous.kind !== incoming.kind
      || previous.schemaVersion !== incoming.schemaVersion
    ) {
      throw new Error(`观察数据 ${incoming.id} 的类型或协议版本不兼容`);
    }
  }

  private assertRevision(
    previous: ObservationDataset,
    incoming: ObservationDataset,
  ): void {
    if (incoming.revision <= previous.revision) {
      throw new ObservationRevisionConflictError(
        incoming.id,
        previous.revision,
        incoming.revision,
      );
    }
  }

  private commit(
    datasetId: string,
    rawDataset: ObservationDataset,
  ): ObservationDataset {
    const previous = this.datasets.get(datasetId);
    if (previous)
      this.assertRevision(previous, rawDataset);
    const dataset = observationDatasetSchema.parse(applyRetention(rawDataset));
    this.datasets.set(datasetId, dataset);
    this.notify(datasetId, dataset);
    return cloneDataset(dataset);
  }

  private notify(
    datasetId: string,
    dataset: ObservationDataset | null,
  ): void {
    if (this.batchDepth > 0) {
      this.pendingNotifications.set(
        datasetId,
        dataset ? cloneDataset(dataset) : null,
      );
      return;
    }
    this.notifyImmediately(datasetId, dataset);
  }

  private flushNotifications(): void {
    const notifications = this.pendingNotifications;
    this.pendingNotifications = new Map();
    for (const [datasetId, dataset] of notifications)
      this.notifyImmediately(datasetId, dataset);
  }

  private notifyImmediately(
    datasetId: string,
    dataset: ObservationDataset | null,
  ): void {
    const snapshot = dataset ? cloneDataset(dataset) : null;
    for (const listener of this.listeners.get(datasetId) ?? []) {
      try {
        listener(snapshot ? cloneDataset(snapshot) : null, datasetId);
      }
      catch {
        // A faulty view subscriber must not interrupt the producer or peers.
      }
    }
    for (const listener of this.allListeners) {
      try {
        listener(snapshot ? cloneDataset(snapshot) : null, datasetId);
      }
      catch {
        // A faulty view subscriber must not interrupt the producer or peers.
      }
    }
  }
}
